/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { designationMasterService, type DesignationMaster } from '../../services/designation-master.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';

const DesignationMasterView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'designation-master',
    basePath: '/designation-master',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.DESIGNATION_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.DESIGNATION_MASTER);

  const [data, setData] = useState<DesignationMaster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Set breadcrumb title to show designation name instead of UUID
  useBreadcrumbTitle(data?.name);

  useEffect(() => {
    // Wait for ID decryption to complete
    if (!isReady || !id) {
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await designationMasterService.getById(id);
        setData(res);
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch designation'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  const handleEdit = () => {
    if (id) navigateToEdit(id);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await designationMasterService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete designation'));
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !data) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Designation not found'} className="mb-4 w-full" />
        <Button
          label="Back to Designations"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  return (
    <div className="p-3" data-testid="SCR-DesignationMaster-View">
      {/* Header Section */}
      <ScreenHeader
        icon="pi pi-id-card"
        title="Designation Details"
        onBack={() => navigateToList()}
      />

      {/* Designation Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          <div className="w-12 h-12 rounded bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
            <i className="pi pi-id-card text-teal-600 dark:text-teal-400" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <h2 className="text-base font-semibold text-ink">{data.name}</h2>
              {(data as any).isDelete || data.isDelete ? (
                <Tag value="Deleted" severity="danger" />
              ) : (
                <Tag value="Active" severity="success" />
              )}
            </div>
            <p className="text-sm text-ink-muted">
              {data.designationCd || 'No designation code'}
            </p>
          </div>
        </div>

        {!((data as any).isDelete || data.isDelete) && (
          <div className="flex gap-2">
            {canUpdate && (
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={handleEdit}
                size="small"
              />
            )}
            {canDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                onClick={handleDeleteClick}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Basic Information */}
        <div className="ds-card p-3">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2.5 flex items-center gap-1.5">
            <i className="pi pi-info-circle text-blue-500" style={{ fontSize: '0.875rem' }} />
            Basic Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <label className="text-xs font-medium text-ink-muted block mb-0.5">Name</label>
              <p className="text-sm text-ink">{data.name || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-muted block mb-0.5">Designation Code</label>
              <p className="text-sm text-ink">{data.designationCd || '-'}</p>
            </div>
          </div>
        </div>

        {/* Audit Information */}
        <AuditInfoCard
          createdAt={data.createdAt}
          updatedAt={data.updatedAt}
          createdBy={data.createdBy}
          updatedBy={data.updatedBy}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        visible={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        message="Are you sure you want to delete this designation? This action will mark it as deleted."
        testId="DesignationMasterView.Dialog.Delete"
      />
    </div>
  );
};

export default DesignationMasterView;
